/**
 * Reprint scope — WHICH placements of a queued garment actually go back on film.
 *
 * ## Why this pure module lives in contracts
 *
 * The same answer is needed in three places that must never disagree: the flush's cost QUOTE (the
 * 422 confirmation the operator approves), the flush's BUILD (the item rows actually written), and
 * the cutting-bench PICKER (the sentence shown next to each garment before it is submitted). Two of
 * those are server, one is the SPA. `@fsp/contracts` is the only package all three already import
 * runtime code from (see `garment-identity.ts` for the same charter), so they call the literal same
 * function rather than three mirrored copies that drift.
 *
 * ## The problem it solves
 *
 * A garment can carry several prints — a front and a back. Ruining one film does not ruin the other:
 * at the cutting bench the shirt is not even in play (cutting precedes pressing) and the front and
 * back are usually nested far apart or on different sheets, so tearing exactly one is the normal
 * case. Reprinting both wastes a film and a press cycle.
 *
 * ## THE INVARIANT
 *
 * **If a replacement blank is drawn, EVERY placement reprints.** A subset onto a fresh shirt is a
 * shirt with a blank front — the garment ships wrong, or does not ship at all. Under-printing is the
 * worst outcome available here. Over-printing costs film, which is recoverable. So every fallback
 * below resolves toward printing MORE, and narrowing is reachable only down one explicit path.
 *
 * Both inputs are monotone toward more printing, which is what makes the invariant hold under
 * concurrent reports rather than merely under review:
 *
 * | input             | mechanism                                        | can only move toward |
 * | ----------------- | ------------------------------------------------ | -------------------- |
 * | `transferOnly`    | one-way `true`→`false` downgrade at the store     | drawing a blank      |
 * | `ruinedLocations` | DDB String Set union via `ADD`                   | a wider subset       |
 *
 * A later reporter can therefore widen the answer but never narrow it, whatever order the reports
 * land in.
 *
 * ## Line-level, not garment-level
 *
 * `rows` is ONE order line's queued garments, and one answer covers all of them. This is forced by
 * storage, not chosen: a batch item is keyed `ITEM#<store>#<order>#<line>#<location>` and carries the
 * copy count as an attribute, so two garments of one line cannot hold two different location sets on
 * one sheet without colliding on that key. Hence the scope is the UNION across the line, and any one
 * garment of the line drawing a blank forces the full set for all of them. Two keep-the-blank
 * garments ruined at different locations both get the union — deliberate over-print, and it keeps
 * `quantity`, `reissueRanks` and `blankConsumables` uniform per line, which every downstream reader
 * already assumes.
 */
/**
 * THE reprint scope decision — the only code anywhere that answers "which placements get reprinted",
 * and the only place the `transferOnly` AND is computed. Deterministic over its inputs, so the quote
 * and the build cannot produce different sheets from the same rows.
 */
export function reprintPlacementsFor(rows) {
    const first = rows[0];
    // No rows is an ABSENCE of information, not an empty subset. Callers never do this (a flush line is
    // built from at least one row), but returning a plausible-looking `[]` would be a sheet with
    // nothing on it — so it is a NAMED reason a caller can refuse on. `transferOnly` is false, because
    // `[].every(…)` is true and "no garments, so no blank needed" is the one place the AND's identity
    // element points the unsafe way.
    if (!first)
        return { placements: [], transferOnly: false, narrowed: false, reason: 'NO_ROWS' };
    // The garment's full frozen placement set. Widest wins if rows ever disagree: the flush refuses
    // divergent lines upstream (INCONSISTENT_PLACEMENTS), so this is unreachable today, and picking the
    // widest keeps even the unreachable path aimed at over-print.
    const all = [
        ...rows.reduce((best, r) => (r.placements.length > best.length ? r.placements : best), first.placements),
    ];
    // ── BRANCH 1: THE INVARIANT ───────────────────────────────────────────────────────────────────
    // A replacement blank is drawn unless EVERY reporter of EVERY garment of this line said the blank
    // survived. `=== true` rather than a truthiness test: an ABSENT flag means the blank is ruined (the
    // safe default the storage layer writes unconditionally), and coercion reads the same here while
    // quietly ceasing to state the rule.
    const transferOnly = rows.every((r) => r.transferOnly === true);
    if (!transferOnly) {
        return { placements: all, transferOnly: false, narrowed: false, reason: 'BLANK_DRAWN' };
    }
    // ── BRANCH 2: UNKNOWN DOMINATES ───────────────────────────────────────────────────────────────
    // A reporter that named NO film has an UNKNOWN scope, not an empty one, and in a lattice ordered by
    // "more printing" unknown is the TOP element. So ONE such report anywhere on the line forces the
    // full set — it can never be out-voted by a reporter that did name a film.
    //
    // ⚠️ THIS IS WHY THE CHECK IS PER ROW AND NOT `ruined.size === 0` ON THE UNION. Testing the union
    // for emptiness only fires when NO reporter anywhere named a film, so a located report and a
    // location-less one on the SAME LINE would narrow to the located one's subset and silently discard
    // the other. Concretely, and this reached a P0 in review: the cutter tears garment #1's back and
    // ticks `{back}`; hours later the presser ruins garment #2's FRONT film, taps "No — transfer only"
    // at the kiosk (which by design sends no location, because a scan names a garment, not a film), and
    // the line narrows to `back` — garment #2 gets a back film it did not need and its ruined front is
    // never reprinted. The order then ships, because marking that sheet printed retires both rows.
    //
    // `wholeGarmentReported` is the DURABLE half of the same rule: a merge collapses two reports onto one
    // row, so without a persisted marker the second reporter's "I named no film" would vanish and this
    // check would see only the first reporter's set. The store writes it unconditionally, exactly like
    // `transferOnly = false`.
    const unknownScope = rows.some((r) => r.wholeGarmentReported === true || (r.ruinedLocations ?? []).length === 0);
    if (unknownScope) {
        return { placements: all, transferOnly: true, narrowed: false, reason: 'NO_SET' };
    }
    const ruined = new Set();
    for (const r of rows)
        for (const loc of r.ruinedLocations ?? [])
            ruined.add(loc);
    // Intersect against the garment's OWN placements. `location` is a free string on both sides, so a
    // typo, a case mismatch, a screen-print location or a pulled-from-stock placement simply fails to
    // match rather than being trusted into the scope.
    const keep = all.filter((p) => ruined.has(p.location));
    // NOTHING MATCHED: the set names no film this garment has on its sheet. Do NOT print nothing — an
    // empty sheet is the one outcome worse than wasted film, because marking it printed would retire
    // the queue row and un-block the order having reprinted zero films.
    if (keep.length === 0) {
        return { placements: all, transferOnly: true, narrowed: false, reason: 'NO_MATCH' };
    }
    // Every placement was reported ruined. Same films as no narrowing at all, reported honestly.
    if (keep.length === all.length) {
        return { placements: keep, transferOnly: true, narrowed: false, reason: 'FULL_SET' };
    }
    return { placements: keep, transferOnly: true, narrowed: true, reason: 'NARROWED' };
}
//# sourceMappingURL=reprint-scope.js.map